import { paths } from '@/app/routes';
import { InstantSearchFieldAdaptor } from '@/components/SearchField';
import { BrokerGroupSelect } from '@/components/selects/BrokerGroupSelect';
import { ClientStatusTableListSelect } from '@/components/selects/ClientStatusTableListSelect';
import { ClientTypeRefinementListSelect } from '@/components/selects/ClientTypeRefinementListSelect';
import { OwnerMultiSelect } from '@/components/selects/OwnerSelect';
import { RefinementListSelect } from '@/components/selects/RefinementListSelect';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { setFilters } from '@/features/table/instantSearchSlice';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useInstantSearchOwnerFilters } from '@/hooks/useInstantSearchOwnerFilters';
import { FormControl, FormLabel, Grid } from '@mui/material';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

const ClientTableFilters = ({
	prospectFilter,
	setProspectFilter,
	tab,
	hideStageFilter = false,
	hideReferralPartner = false,
}) => {
	const dispatch = useDispatch();

	const gridRef = useRef();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const location = useLocation();

	const isProspectView = location.pathname.includes(paths.prospects);

	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);

	const userDetailsUserId = userDetails?.userId;

	const { getFeatureFlags } = useFeatureFlagContext();
	const showProspects = Boolean(getFeatureFlags('prospects')?.active);
	const showClientStatus = Boolean(getFeatureFlags('clientStatusFilter')?.active);
	const showReferralPartner = Boolean(getFeatureFlags('showReferralPartner')?.active);

	const { pathname: slug } = useLocation();
	const { filters, owners: clientOwnerIds } = useInstantSearchOwnerFilters();

	const [columns, setColumns] = useState(12);

	useLayoutEffect(() => {
		if (gridRef.current) {
			const cols = Array.from(gridRef.current.children).length;
			setColumns((cols - 1) * 2 + 4);
		}
	}, []);

	return (
		<Grid ref={gridRef} container columns={columns} py={1} spacing={1}>
			<Grid size={{ xs: 12, sm: 4 }}>
				<FormLabel>Search</FormLabel>
				<InstantSearchFieldAdaptor />
			</Grid>
			<Grid size={{ xs: 12, sm: 2 }}>
				<FormControl fullWidth>
					<FormLabel>Client Owner</FormLabel>
					<OwnerMultiSelect
						users={clientOwnerIds}
						disabled={!userDetails?.canViewGroup}
						isUserFixed={(user) => {
							if (user.id === userDetailsUserId)
								return clientOwnerIds.length === 1;
							return false;
						}}
						onChange={(value) => {
							const newIds = [value].flat();
							dispatch(
								setFilters({
									slug,
									type: 'owners',
									values: newIds.length === 0 ? [userDetailsUserId] : newIds,
								}),
							);
						}}
						returnValue='id'
						disableClearable={true}
					/>
				</FormControl>
			</Grid>
			<Grid size={{ xs: 12, sm: 2 }}>
				<FormControl fullWidth>
					<FormLabel>Broker Group</FormLabel>
					<BrokerGroupSelect
						multiple={true}
						showCheckbox={true}
						brokerGroupId={filters?.brokerGroupId || []}
						onChange={(newValues) => {
							dispatch(
								setFilters({
									slug,
									type: 'brokerGroupId',
									values: newValues,
								}),
							);
						}}
						disableClearable={false}
					/>
				</FormControl>
			</Grid>

			{showReferralPartner && !hideReferralPartner && (
				<Grid size={{ xs: 12, sm: 2 }}>
					<RefinementListSelect
						attribute='referralPartner'
						title='Referral partner'
						multiple
					/>
				</Grid>
			)}
			{!isProspectView && (
				<>
					{showProspects && (
						<Grid size={{ xs: 12, sm: 2 }}>
							<ClientTypeRefinementListSelect
								attribute='isProspect'
								title='Client Type'
								prospectFilter={prospectFilter}
								setProspectFilter={setProspectFilter}
							/>
						</Grid>
					)}
					{showClientStatus && (
						<Grid size={{ xs: 12, sm: 2 }}>
							<ClientStatusTableListSelect displayTitle={true} />
						</Grid>
					)}
				</>
			)}
			{showProspects && isProspectView && tab !== 'closed' && !hideStageFilter && (
				<Grid size={{ xs: 12, sm: 2 }}>
					<FormControl fullWidth>
						<FormLabel>Prospect stage</FormLabel>
						<RefinementListSelect attribute='stage' multiple />
					</FormControl>
				</Grid>
			)}
		</Grid>
	);
};
export default ClientTableFilters;
